import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { FC, useEffect, useState } from 'react'
import { apiClient } from '../api/configs/axios-setup'
import { SelectOption } from '../types/types'

interface DatabricksSolveEntry {
  run: string
  state_name: string
  compliance_team: string | null
  solve_count: string
  identifiers: string
}

interface ComplianceSolveRow {
  run: string
  stateName: string
  complianceTeam: string
  rawSolves: number
  complianceSolves: number
  identifiers: string
}

/**
 * NASCAR-Cup compliance rule:
 * A case can only be solved twice to count as 1 solve.
 * If solved a 3rd time, that 3rd+4th solve count as a separate compliance solve.
 * Formula: complianceSolves = Math.ceil(rawSolves / 2)
 */
const calcComplianceSolves = (rawSolves: number): number => {
  return Math.ceil(rawSolves / 2)
}

const currentYear = new Date().getFullYear()
const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0')

const monthOptions: SelectOption[] = [
  { label: 'All Months', value: 'all' },
  { label: 'January', value: '01' },
  { label: 'February', value: '02' },
  { label: 'March', value: '03' },
  { label: 'April', value: '04' },
  { label: 'May', value: '05' },
  { label: 'June', value: '06' },
  { label: 'July', value: '07' },
  { label: 'August', value: '08' },
  { label: 'September', value: '09' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' }
]

const yearOptions: SelectOption[] = [
  { label: (currentYear - 1).toString(), value: (currentYear - 1).toString() },
  { label: currentYear.toString(), value: currentYear.toString() }
]

const SolveCountCompliance: FC = () => {
  const [solveData, setSolveData] = useState<ComplianceSolveRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth)
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString())
  const [selectedOrg, setSelectedOrg] = useState<string>('all')

  const orgOptions: SelectOption[] = [
    { label: 'All Organizations', value: 'all' },
    { label: 'Aero', value: 'Aero' },
    { label: 'Vehicle Dynamics', value: 'Vehicle Dynamics' }
  ]

  // Fetch solve counts from Databricks compliance table
  useEffect(() => {
    const fetchSolveCounts = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get('/Reports/nascar/solve-counts', {
          params: { month: selectedMonth, year: selectedYear, org: selectedOrg }
        })
        const entries: DatabricksSolveEntry[] = res.data || []
        const rows: ComplianceSolveRow[] = entries.map(entry => {
          const rawSolves = parseInt(entry.solve_count, 10) || 0
          return {
            run: entry.run,
            stateName: entry.state_name || '',
            complianceTeam: entry.compliance_team || 'N/A',
            rawSolves,
            complianceSolves: calcComplianceSolves(rawSolves),
            identifiers: entry.identifiers || ''
          }
        })
        setSolveData(rows)
      } catch (err: any) {
        setError(err?.response?.data || err.message || 'Failed to fetch solve counts')
        setSolveData([])
      } finally {
        setLoading(false)
      }
    }
    fetchSolveCounts()
  }, [selectedMonth, selectedYear, selectedOrg])

  const totalRawSolves = solveData.reduce((sum, r) => sum + r.rawSolves, 0)
  const totalComplianceSolves = solveData.reduce((sum, r) => sum + r.complianceSolves, 0)

  return (
    <div className="p-4 w-full">
      <h1 className="text-2xl font-bold text-gm-blue mb-2">Solve Count Compliance</h1>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        NASCAR-Cup rule: Every 2 solve runs of a case counts as 1 compliance solve. A 3rd and 4th
        solve of the same case counts as a separate compliance solve.
      </p>

      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="font-semibold text-sm">Month:</label>
          <Dropdown
            value={selectedMonth}
            options={monthOptions}
            onChange={e => setSelectedMonth(e.value)}
            className="w-40"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="font-semibold text-sm">Year:</label>
          <Dropdown
            value={selectedYear}
            options={yearOptions}
            onChange={e => setSelectedYear(e.value)}
            className="w-28"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="font-semibold text-sm">Org:</label>
          <Dropdown
            value={selectedOrg}
            options={orgOptions}
            onChange={e => setSelectedOrg(e.value)}
            className="w-48"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}

      <div className="flex gap-6 mb-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
          <div className="text-3xl font-bold text-gm-blue">{totalComplianceSolves}</div>
          <div className="text-xs text-gray-500 mt-1">Total Compliance Solves</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
          <div className="text-3xl font-bold text-gray-600">{totalRawSolves}</div>
          <div className="text-xs text-gray-500 mt-1">Total Raw Solve Runs</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
          <div className="text-3xl font-bold text-gray-600">{solveData.length}</div>
          <div className="text-xs text-gray-500 mt-1">Unique Cases</div>
        </div>
      </div>

      <DataTable
        value={solveData}
        loading={loading}
        paginator
        rows={25}
        rowsPerPageOptions={[10, 25, 50, 100]}
        emptyMessage="No solve count data found."
        sortField="complianceSolves"
        sortOrder={-1}
        className="text-sm">
        <Column field="run" header="Run" sortable />
        <Column field="stateName" header="State Name" sortable />
        <Column field="complianceTeam" header="Org" sortable />
        <Column field="rawSolves" header="Raw Solve Runs" sortable className="text-center" />
        <Column
          field="complianceSolves"
          header="Compliance Solves"
          sortable
          className="text-center"
          body={(row: ComplianceSolveRow) => (
            <span className="font-bold text-gm-blue">{row.complianceSolves}</span>
          )}
        />
        <Column
          header="Breakdown"
          body={(row: ComplianceSolveRow) => {
            const ids = row.identifiers ? row.identifiers.split(', ') : []
            return (
              <div className="text-gray-500">
                <div>
                  {row.complianceSolves} compliance solve(s) from {row.rawSolves} run(s)
                </div>
                {ids.length > 0 && (
                  <div className="text-xs mt-1 text-gray-400">IDs: {ids.join(', ')}</div>
                )}
              </div>
            )
          }}
        />
      </DataTable>

      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded border text-sm">
        <h3 className="font-bold mb-2">NASCAR-Cup Compliance Counting Rules</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
          <li>
            Each case attitude/state can be solved up to 2 times ={' '}
            <strong>1 compliance solve</strong>
          </li>
          <li>A 3rd solve starts a new compliance solve (3rd + 4th = 2nd compliance solve)</li>
          <li>
            Formula: <code>compliance_solves = ceil(raw_solves / 2)</code>
          </li>
          <li>Example: 4 solve runs of a case = 2 compliance solves total</li>
          <li>Example: 5 solve runs of a case = 3 compliance solves total</li>
        </ul>
      </div>
    </div>
  )
}

export default SolveCountCompliance
